import React, { useEffect, useState } from "react";
import { GenericInput } from "../components/input/GenericInput";
import { Stack } from "@mui/material";
import { FormWrapper } from "../components/styled/StyledComponents";
import {
  sActive,
  sAddProduct,
  sProducts,
  sUGX,
  sUpdate,
} from "../constants/strings";
import { GenericSelect } from "../components/select/GenericSelect";
import { FileUploader } from "../components/upload/FileUploader";
import { FormButton } from "../components/buttons/FormButton";
import { GenericSwitch } from "../components/elements/Switch";
import { RowWrapperMui } from "../components/styled/MuiComponents";
import { ImageWrapper } from "../components/image/ImageViewer";
import { useFirebase } from "../hooks/firebase/useFirebase";
import { PRODUCTS_API, SHOPS_API } from "../constants/api";
import { useAppContext } from "../context/useAppContext";
import useForm from "../hooks/useForm";
import { TabView, createTab } from "../components/elements/TabView";
import { useUpload } from "../hooks/useUpload";
import { createKeywords } from "../utils/keywords";
import { AutofillForm, capitalizeText } from "../utils/helpers";
import { sortByAlpha } from "../utils/filterUtils";
import { Visibility } from "../components/common/Visibility";
import { IN_STOCK, OUT_OF_STOCK, VARIABLE_STOCK } from "../constants/webapp";
import { useReportStats } from "../hooks/common/useCommon";
import { serverTimestamp } from "firebase/firestore";

export const stock_types = [
  {
    label: "In Stock",
    value: IN_STOCK,
  },
  {
    label: "Variable Stock",
    value: VARIABLE_STOCK,
  },
  {
    label: "Out of Stock",
    value: OUT_OF_STOCK,
  },
];

export const ProductForm = ({ data: productData, setOpen }) => {
  const {
    getData: getShops,
    data: shops,
    addData: addProduct,
    loading,
    updateData,
  } = useFirebase();

  // update reports
  const { updateReportStats } = useReportStats();

  const {
    imageUrls: uploadedUrls,
    uploadImages,
    deleteImages,
    loading: uploading,
  } = useUpload();

  const { form, onChange, updateFormValue, updateFormValues, setForm } =
    useForm({
      active: true,
      category: [],
      imageUrls: [],
      keywords: [],
      tags: [],
      del_fee: 500,
      desc: "",
      sold: 0,
      stock: 0,
      stock_type: 1,
      original_price: 0,
    });

  const { contextData } = useAppContext();
  const { categories } = contextData;
  const [images, setImages] = useState([]);
  const [delArray, setDelArray] = useState([]);

  // const [imageUrls, setImageUrls] = useState([]);

  // useEffect(() => {
  //   if (data) {
  //     updateFormValues(data);
  //   }
  // }, [data]);

  // useEffect(() => {
  //   if (form.product_name) console.log(createKeywords(form.product_name));
  // }, [form.product_name]);

  AutofillForm(productData, setForm);

  useEffect(() => {
    getShops(SHOPS_API);
  }, []);

  function cleanUp() {
    updateFormValues({
      product_name: "",
      desc: "",
      sold: 0,
      stock: 0,
      stock_type: 1,
      original_price: 0,
      price: 0,
      del_fee: 500,
      category: [],
      imageUrls: [],
      keywords: [],
      tags: [],
    });
    setImages([]);
    setDelArray([]);
  }

  function setTypes() {
    // prevent string inputs
    if (form.del_fee === "") form.del_fee = 0;
    if (form.original_price === "") form.original_price = 0;
    if (form.sold === "") form.sold = 0;
    if (form.stock === "") form.stock = 0;
    if (form.price === "") form.price = 0;
  }

  const setDocData = () => {
    setTypes();
    // update form
    form.imageUrls = uploadedUrls;
    form.timestamp = serverTimestamp();
    form.keywords = createKeywords(form.product_name);

    addProduct(PRODUCTS_API, form, cleanUp());
    // update report
    updateReportStats(sProducts, 1);
  };

  function handleAddProduct() {
    // handle image upload
    uploadImages(images, sProducts, setDocData);
  }

  // update product
  async function updateProduct() {
    // update images
    await uploadImages(images, sProducts, null, true);
    form.imageUrls = [...form.imageUrls, ...uploadedUrls];
    // delete images
    await deleteImages(delArray);

    setTypes();
    updateData(productData.docRef, form).then(() => {
      setOpen(false);
      cleanUp();
    });
  }

  const onSubmit = (e) => {
    e.preventDefault();
    if (productData) {
      form.keywords = createKeywords(form.product_name);

      updateProduct();
    } else {
      handleAddProduct();
    }
  };

  // remove product image
  function deleteImage(imageUrl) {
    // filter array to remove imageUrl
    var array = productData?.imageUrls.filter((v) => v !== imageUrl);
    // update current list of images
    productData.imageUrls = array;
    // update form url values
    form.imageUrls = array;
    // set delete array and re-render view
    setDelArray([...delArray, imageUrl]);
  }

  const productDetails = () => {
    return (
      <FormWrapper>
        <form onSubmit={onSubmit}>
          <Stack spacing={3}>
            {/* Images */}
            <ImageWrapper
              images={productData?.imageUrls}
              handleDelete={deleteImage}
            />
            .{/* Name */}
            <GenericInput
              label="Product Name"
              helperText="Do not exceed 20 characters."
              name="product_name"
              value={form.product_name}
              onChange={(e) => capitalizeText(e, updateFormValues)}
              required
            />
            <RowWrapperMui>
              {/* Price */}
              <GenericInput
                label="Price"
                onChange={onChange}
                name="price"
                suffix={sUGX}
                value={form.price}
                type="number"
                required
              />

              {/* Original */}
              <GenericInput
                name="original_price"
                value={form?.original_price}
                onChange={onChange}
                label="Original Price"
                suffix={sUGX}
                type="number"
              />
            </RowWrapperMui>
            {/* Summary */}
            <GenericInput
              label="Description"
              placeholder="Product Summary"
              name="desc"
              onChange={onChange}
              value={form.desc}
              rows={3}
              helperText="Detailed summary of product specifications, dimesions, sizes etc."
            />
            {/* Categories */}
            <GenericSelect
              label="Category"
              mode="multiple"
              value={form.category}
              options={sortByAlpha(categories, "category_name")?.map((item) => {
                return {
                  value: item.id,
                  label: item.category_name,
                };
              })}
              onChange={(value) => {
                updateFormValues({ category: value });
              }}
              helperText="You can select multiple categories for recommendation."
              required
            />
            {/* Tags */}
            <GenericSelect
              label="Tags"
              mode="tags"
              value={form?.tags}
              onChange={(value) => {
                updateFormValues({ tags: value });
              }}
              helperText="You can create multiple tags to ease reference."
              optional
            />
            {/* SHOPS */}
            <GenericSelect
              label="Shop"
              value={form.shop}
              onChange={(value) => {
                updateFormValue("shop", value);
              }}
              options={shops?.map((item) => {
                return {
                  value: item.id,
                  label: item.shop_name,
                };
              })}
              required
            />
            <FileUploader
              images={images}
              setImages={setImages}
              required
              size={0.2}
              label="Product Images"
              helperText="Maximum upload limit 3 images. Minimum resolution 500 x 500 (1:1) recommended, upto 200KB each. Comply with the background color standards."
            />
            {/* Switch */}
            <GenericSwitch
              checked={form.active}
              label={sActive}
              updateFormValue={updateFormValue}
            />
            <RowWrapperMui>
              {/* Submit */}
              <div className="flexColumn x100">
                <FormButton loading={uploading || loading}>
                  {productData ? sUpdate : sAddProduct}
                </FormButton>
              </div>
              <FormButton type="default">Save Product</FormButton>
            </RowWrapperMui>
          </Stack>
        </form>
      </FormWrapper>
    );
  };

  const productSpecs = () => {
    return (
      <FormWrapper>
        <Stack spacing={3}>
          {/* Del fee */}
          <GenericInput
            name="del_fee"
            value={form?.del_fee}
            onChange={onChange}
            label="Delivery Fee"
            suffix={sUGX}
            type="number"
          />
          <GenericSelect
            label="Stock Type"
            value={form.stock_type}
            onChange={(value) => {
              updateFormValue("stock_type", value);
            }}
            options={stock_types}
            required
          />
          <Visibility show={form?.stock_type === 2}>
            <GenericInput
              label="Stock Amount"
              name="stock"
              type="number"
              placeholder="Available Stock"
              onChange={onChange}
              value={form.stock}
            />
          </Visibility>

          <GenericInput
            label="Sold Quantity"
            name="sold"
            type="number"
            onChange={onChange}
            value={form.sold}
          />
        </Stack>
      </FormWrapper>
    );
  };

  const productTabs = [
    createTab("Details", productDetails()),
    createTab("Specifications", productSpecs()),
  ];

  return <TabView items={productTabs} />;
};
